import { z } from 'zod'
import {
  CRM_COMPANY_READ_CONTRACT_VERSION,
  crmCompanyReadSideEffectsSchema,
  crmContactSummarySchema,
} from './crm-company-read'
import {
  CRM_MESSAGE_CONTRACT_VERSION,
  crmMessageSummarySchema,
} from './crm-message-read'

/** Write-path side effects allow db1/audit true while keeping closed flags fixed. */
export const crmContactMessageWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

/**
 * Transport contracts for CRM contact create/patch and message reprocess.
 * Request schemas deliberately stay loose and passthrough so they accept
 * exactly what today's handlers accept (including legacy snake_case aliases).
 * Domain validation and 400 precedence remain inside the handwritten handlers.
 */

const optionalUnknown = z.unknown().optional()

export const crmContactCreateRequestSchema = z.object({
  fullName: optionalUnknown,
  full_name: optionalUnknown,
  name: optionalUnknown,
  emails: optionalUnknown,
  phones: optionalUnknown,
  whatsappE164: optionalUnknown,
  whatsapp_e164: optionalUnknown,
  title: optionalUnknown,
  platformUserId: optionalUnknown,
  platform_user_id: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmContactPatchRequestSchema = z.object({
  fullName: optionalUnknown,
  full_name: optionalUnknown,
  name: optionalUnknown,
  emails: optionalUnknown,
  phones: optionalUnknown,
  whatsappE164: optionalUnknown,
  whatsapp_e164: optionalUnknown,
  title: optionalUnknown,
  platformUserId: optionalUnknown,
  platform_user_id: optionalUnknown,
  active: optionalUnknown,
}).passthrough()

/** Reprocess accepts an empty or arbitrary body; handlers ignore the body. */
export const crmMessageReprocessRequestSchema = z.object({}).passthrough()

export const crmContactCreateResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  contact: crmContactSummarySchema,
  replayed: z.boolean(),
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmContactPatchResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  contact: crmContactSummarySchema,
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmMessageReprocessResponseSchema = z.object({
  contract_version: z.literal(CRM_MESSAGE_CONTRACT_VERSION),
  message: crmMessageSummarySchema,
  reEnqueued: z.boolean(),
  sideEffects: crmContactMessageWriteSideEffectsSchema,
}).passthrough()

export const CRM_CONTACT_MESSAGE_WRITE_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_IDEMPOTENCY_KEY_REUSED',
] as const

export const crmContactMessageWriteErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.enum(CRM_CONTACT_MESSAGE_WRITE_ERROR_CODES),
    message: z.string(),
  }).passthrough(),
  reference: z.string().optional(),
}).passthrough()

export type CrmContactCreateRequest = z.infer<typeof crmContactCreateRequestSchema>
export type CrmContactPatchRequest = z.infer<typeof crmContactPatchRequestSchema>
export type CrmMessageReprocessRequest = z.infer<typeof crmMessageReprocessRequestSchema>
export type CrmContactCreateResponse = z.infer<typeof crmContactCreateResponseSchema>
export type CrmContactPatchResponse = z.infer<typeof crmContactPatchResponseSchema>
export type CrmMessageReprocessResponse = z.infer<typeof crmMessageReprocessResponseSchema>
export type CrmContactMessageWriteErrorCode = (typeof CRM_CONTACT_MESSAGE_WRITE_ERROR_CODES)[number]
export type CrmContactMessageWriteErrorEnvelope = z.infer<typeof crmContactMessageWriteErrorEnvelopeSchema>
